import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { BaseButton } from 'iebt-zing-storybook'
import React, { useEffect, useState } from 'react'
import { Row } from 'react-bootstrap'
import { Loading } from 'components/loading'
import { trans } from 'sharedKernel/i18n'
import {
  ChangeDataButtonArea,
  EditIconButton,
  FormChallengeHeader,
  FormIdeaSectionName,
  FormIdeaSections,
  LoadindContainer,
  PageHeaderRow,
  SolutionTitle,
  TitleGroup
} from './style'
import { SolutionData, SolutionInfoVisualizationProps, UpdateSolutionTitleRequest } from './contracts'
import { FormAnswer } from 'components/Evaluation/Form/Answer/FormAnswer'
import { api, GetFormat } from 'sharedKernel/api'
import { SwiperNavMenu } from 'components/swiper/swiperNav/swiperNavMenu'
import { showModal } from 'components/popup'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons'
import Tippy from '@tippyjs/react'
import 'tippy.js/dist/tippy.css'

interface FormStep {
  name: string
  component: JSX.Element
}

export const SolutionInfoVisualization = ({
  solutionId,
  challengeId
}: SolutionInfoVisualizationProps): JSX.Element => {
  const navigate = useNavigate()
  const [activeSection, setActiveSection] = useState(0)
  const [solutionData, setSolutionData] = useState<SolutionData>()
  const [isUpdatingTitle, setIsUpdatingTitle] = useState(false)

  const getRequest: GetFormat = {
    url: `/v1/hub/company/solution/${solutionId}`
  }

  useEffect(() => {
    api.get(getRequest.url).then((response) => {
      setSolutionData(response.data)
    })
  }, [getRequest.url])

  const labels = {
    formSection: trans('solution.subscription.formSection'),
    back: trans('Back'),
    editTitle: trans('solution.title.editModal.title'),
    successTitle: trans('solution.title.toast.successTitle'),
    updateSuccess: trans('solution.title.toast.updateSuccess'),
    errorTitle: trans('solution.title.toast.errorTitle'),
    updateError: trans('solution.title.toast.updateError')
  }

  const updateSolutionTitle = async (newTitle: string): Promise<void> => {
    setIsUpdatingTitle(true)

    try {
      const payload: UpdateSolutionTitleRequest = { title: newTitle }
      await api.patch(`/v1/hub/company/solution/${solutionId}/title`, payload)
      setSolutionData((prev) => {
        if (prev == null) return prev

        return {
          ...prev,
          data: {
            ...prev.data,
            title: newTitle
          }
        }
      })

      showModal('single', {
        title: labels.successTitle,
        message: labels.updateSuccess
      })
    } catch {
      showModal('single', {
        title: labels.errorTitle,
        message: labels.updateError
      })
    } finally {
      setIsUpdatingTitle(false)
    }
  }

  const handleEditTitle = (): void => {
    const currentTitle = solutionData?.data?.title
    if (currentTitle == null || currentTitle === '') return

    showModal('editTitle', {
      currentTitle,
      onConfirm: (newTitle: string) => {
        void updateSolutionTitle(newTitle)
      }
    })
  }

  const buildBreakpoints = (): Record<string, { slidesPerView: number, spaceBetween: number }> => ({
    250: { slidesPerView: 2, spaceBetween: 0 },
    450: { slidesPerView: 3, spaceBetween: 0 },
    700: { slidesPerView: 4, spaceBetween: 0 },
    900: { slidesPerView: 5, spaceBetween: 0 }
  })

  const buildNavItems = (): Array<{ name: string, itemHash: string }> => ([
    {
      name: labels.formSection,
      itemHash: 'SubscriptionForm'
    }
  ])

  const buildSteps = (sections: any[]): FormStep[] => {
    return sections.map((section, sectionIndex) => ({
      name: section.id,
      component: (
        <div className='mt-2' key={`${sectionIndex}section`}>
          <div className='d-flex flex-column'>
            <FormIdeaSectionName>{String(section.name)}</FormIdeaSectionName>
            <hr />
          </div>
          {section.answers?.map((answer, answerIndex) => (
            <FormAnswer key={`${answerIndex}answer`} answer={answer} />
          ))}
        </div>
      )
    }))
  }

  if (solutionData == null) {
    return (
      <LoadindContainer>
        <Loading />
      </LoadindContainer>
    )
  }

  const formSteps = buildSteps(solutionData.data.sections)

  return (
    <>
      <div className='mb-6'>
        <PageHeaderRow>
          <TitleGroup>
            <SolutionTitle>{solutionData.data.title}</SolutionTitle>
            <Tippy content={labels.editTitle}>
              <EditIconButton
                onClick={handleEditTitle}
                disabled={isUpdatingTitle}
              >
                <FontAwesomeIcon icon={faPencilAlt} />
              </EditIconButton>
            </Tippy>
          </TitleGroup>
          <ChangeDataButtonArea>
            <BaseButton
              btnContent={labels.back}
              size="big"
              variant='secondary'
              onClick={() => navigate(`/hub/company/challenge/${challengeId}/solutions`)}
            />
          </ChangeDataButtonArea>
        </PageHeaderRow>
        <FormChallengeHeader>
          <SwiperNavMenu
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            breakpoints={buildBreakpoints()}
            carouselNavItems={buildNavItems()}
          />
        </FormChallengeHeader>
      </div>
      <Row>
        <FormIdeaSections id='FormIdeaSections'>
          {formSteps.map((step) => (
            <section id={step.name} key={step.name}>
              {step.component}
            </section>
          ))}
        </FormIdeaSections>
      </Row>
    </>
  )
}